import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Receipt, Loader2, Building2, Route as RouteIcon, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import QuickGastoModal from "@/components/QuickGastoModal";
import { cloudinaryThumb } from "@/lib/cloudinary";
import { useAuth } from "@/context/AuthContext";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

/**
 * Bloque de Gastos asociado a una Petición o a una Ruta.
 * Props:
 *  - peticionId? (para mostrar /peticiones/{id}/gastos con reparto)
 *  - rutaId? (para mostrar /rutas/{id}/gastos con reparto entre clientes)
 */
export default function GastosBlock({ peticionId, rutaId }) {
  const { user, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (peticionId) {
        const { data } = await api.get(`/peticiones/${peticionId}/gastos`);
        setData(data);
      } else if (rutaId) {
        const { data } = await api.get(`/rutas/${rutaId}/gastos`);
        setData(data);
      }
    } catch (err) {
      toast.error("Error cargando gastos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peticionId, rutaId]);

  const remove = async (g) => {
    if (!window.confirm(`¿Eliminar gasto de ${eur(g.importe)}?`)) return;
    try {
      await api.delete(`/gastos/${g.id}`);
      toast.success("Gasto eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-zinc-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando gastos…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="gastos-block">
      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {peticionId ? (
          <>
            <KPI label="Gastos directos" value={eur(data.total_directo)} testid="kpi-directo" />
            <KPI label="Reparto rutas" value={eur(data.total_reparto)} testid="kpi-reparto" accent="text-sky-300" />
            <KPI label="Total imputado" value={eur(data.total_gastos_imputados)} highlight testid="kpi-total-imp" />
          </>
        ) : (
          <>
            <KPI label="Total gastos ruta" value={eur(data.total)} highlight testid="kpi-total-ruta" />
            <KPI label="Componentes" value={data.total_componentes} accent="text-amber-300" testid="kpi-componentes" />
            <KPI label="Repartos" value={(data.reparto || []).length} testid="kpi-num-reparto" />
          </>
        )}
      </div>

      {/* Acción */}
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} size="sm"
          className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="block-new-gasto-btn">
          <Plus className="w-4 h-4 mr-2" /> Añadir gasto
        </Button>
        <QuickGastoModal
          open={open} onOpenChange={setOpen}
          peticionId={peticionId} rutaId={rutaId}
          onSaved={load}
          title={peticionId ? "Gasto · imputado a esta campaña" : "Gasto · imputado a esta ruta"}
        />
      </div>

      {/* Lista de gastos directos */}
      {(data.directos || []).length === 0 ? (
        <div className="text-center py-8 text-zinc-500 border border-dashed border-zinc-800 rounded-lg">
          <Receipt className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No hay gastos registrados.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.directos.map((g) => {
            const puedeEditar = isAdmin || g.operario_id === user?.id;
            return (
              <div key={g.id}
                className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 flex items-center gap-3"
                data-testid={`block-gasto-${g.id}`}>
                {g.ticket_url ? (
                  <a href={g.ticket_url} target="_blank" rel="noreferrer">
                    <img src={cloudinaryThumb(g.ticket_url, 60, 60)} alt="ticket"
                      className="w-12 h-12 rounded object-cover border border-zinc-700" />
                  </a>
                ) : (
                  <div className="w-12 h-12 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-600">
                    <Receipt className="w-5 h-5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="text-zinc-400">{g.fecha}</span>
                    <span className="text-zinc-500">· {g.tipo_gasto}</span>
                    <span className="text-zinc-500">· {g.metodo_pago}</span>
                  </div>
                  <div className="text-sm text-zinc-100 mt-0.5 truncate">
                    {g.concepto || <span className="italic text-zinc-500">— sin concepto —</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {g.operario_nombre || g.operario_email || "?"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-base font-bold text-zinc-50">{eur(g.importe)}</div>
                  {puedeEditar && (
                    <Button size="icon" variant="ghost" onClick={() => remove(g)}
                      className="h-6 w-6 text-zinc-400 hover:text-red-400"
                      data-testid={`block-gasto-delete-${g.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reparto (peticion: rutas; ruta: clientes) */}
      {peticionId && (data.rutas_repartidas || []).length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2 mt-4">
            Rutas con gastos repartidos a esta campaña
          </h4>
          <div className="space-y-1.5">
            {data.rutas_repartidas.map((r) => (
              <div key={r.ruta_id}
                className="bg-zinc-900/30 border border-zinc-800/60 rounded p-2.5 flex items-center justify-between text-xs"
                data-testid={`reparto-${r.ruta_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <RouteIcon className="w-3.5 h-3.5 text-sky-400" />
                  <span className="text-zinc-300 truncate">{r.ruta_codigo || r.ruta_nombre}</span>
                  <span className="text-zinc-500">({r.ruta_fecha})</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-zinc-500">{r.componentes_peticion}/{r.componentes_total_ruta} comp.</span>
                  <span className="text-zinc-500">· {(r.ratio * 100).toFixed(1)}%</span>
                  <span className="text-zinc-500 line-through">{eur(r.gastos_total_ruta)}</span>
                  <span className="text-sky-300 font-semibold">{eur(r.imputado_a_peticion)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {rutaId && (data.reparto || []).length > 0 && (
        <div>
          <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2 mt-4">
            Reparto entre campañas (proporcional a componentes)
          </h4>
          <div className="space-y-1.5">
            {data.reparto.map((r) => (
              <div key={r.peticion_id}
                className="bg-zinc-900/30 border border-zinc-800/60 rounded p-2.5 flex items-center justify-between text-xs"
                data-testid={`reparto-${r.peticion_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-zinc-300 truncate">{r.peticion_codigo || r.peticion_nombre}</span>
                  {r.cliente_nombre && <span className="text-zinc-500 truncate">· {r.cliente_nombre}</span>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-zinc-500">{r.componentes} comp.</span>
                  <span className="text-zinc-500">· {(r.ratio * 100).toFixed(1)}%</span>
                  <span className="text-emerald-300 font-semibold">{eur(r.imputado)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, accent = "text-zinc-100", highlight = false, testid }) {
  return (
    <div className={`rounded-lg border ${highlight ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/40"} px-3 py-2`}
      data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-base font-bold ${highlight ? "text-amber-300" : accent}`}>{value}</div>
    </div>
  );
}
